import { TraversalPathMatchingType } from '@ardoq/api-types';
import { emptyComponentTypeHierarchies } from 'streams/componentTypeHierarchies/componentTypeHierarchyOperations';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import { TraversalBuilderState, TraversalBuilderViewState } from '../types';
import { getEmptySelectedNodeData } from './getEmptySelectedNodeData';
import { setInstanceCounts } from './instanceCounts';
import { getTraversalAsSavableAttributes } from './getTraversals';
import { viewpointBuilderFiltersOperations } from './viewpointBuilderFiltersOperations';

// `locale` is intentionally excluded here: it holds a date-fns `Locale`
// (a frozen object of functions) that `structuredClone` cannot copy. It is an
// immutable singleton, so `getInitialTraversalEditState` grafts the shared
// reference on after cloning the mutable state below.
const initialTraversalEditState: Omit<TraversalBuilderState, 'locale'> = {
  metaModelTriples: [],
  componentTypeHierarchies: emptyComponentTypeHierarchies,
  filters: {},
  baseGraph: {
    sourceMap: new Map(),
    targetMap: new Map(),
    referenceMap: new Map(),
  },
  triples: new Map(),
  selectedGraph: {
    sourceMap: new Map(),
    targetMap: new Map(),
    referenceMap: new Map(),
  },
  metaModel: null,
  startComponentType: null,
  startComponentTypeId: null,
  startNode: null,
  startSetCounts: null,
  startSetExpansion: null,
  startSetExpansionState: null,
  // Maps
  rawGraphNodes: new Map(),
  rawGraphEdges: new Map(),
  graphNodes: new Map(),
  graphEdges: new Map(),
  graphGroups: new Map(),
  instanceCounts: new Map(),
  selectedGraphNodeId: null,
  selectedGraphEdgeId: null,
  tripleOptions: {
    incoming: [],
    outgoing: [],
    selectedNode: getEmptySelectedNodeData(),
    fetchCountsArgs: null,
    optionCountLoadingState: 'idle',
    showOnlyOptionsWithInstanceCounts: true,
    instanceCountsResponse: null,
  },
  hasTripleOptions: {
    outgoingReferences: false,
    incomingReferences: false,
  },
  filteredTripleOptions: {
    incoming: [],
    outgoing: [],
    selectedNode: getEmptySelectedNodeData(),
    fetchCountsArgs: null,
    optionCountLoadingState: 'idle',
    showOnlyOptionsWithInstanceCounts: true,
    instanceCountsResponse: null,
  },
  initialPaths: null,
  traversalStartSet: null,
  traversalStartQuery: null,
  fieldMap: new Map(),
  componentNameAndFieldsIndex: new Map(),
  referenceNameAndFieldsIndex: new Map(),
  componentNameAndWorkspacesIndex: new Map(),
  triplesSortAndFiltersState: {
    filterTerm: '',
    showOnlyOptionsWithInstanceCounts: true,
    tripleSortOrder: 'reference_type_a_z',
  },
  initialFilters: null,
  shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings: true,
  isMetaModelLoaded: false,
  referenceTypes: [],
  pathMatching: TraversalPathMatchingType.LOOSE,
  initialPathCollapsingRules: null,
  pathCollapsing: {
    rules: [],
    currentEditableRule: null,
  },
  shouldIncludeInstanceCounts: true,
  instanceCountsVersion: 0,
  requiredNodeIds: [],
  highlightedPaths: {},
  isInitialPath: true,
  wildcardState: null,
};

export const getInitialTraversalEditState = (): TraversalBuilderState => ({
  ...structuredClone(initialTraversalEditState),
  locale: ARDOQ_DEFAULT_LOCALE,
});

export const getInitialTraversalBuilderViewState =
  (): TraversalBuilderViewState => {
    const initialState = getInitialTraversalEditState();
    return {
      ...initialState,
      triplesSortAndFiltersState: {
        ...initialState.triplesSortAndFiltersState,
        incomingTriplesHiddenCount: 0,
        outgoingTriplesHiddenCount: 0,
      },
      filtersDefinitions:
        viewpointBuilderFiltersOperations.getFiltersDefinitions(initialState),
      asyncLoaders:
        viewpointBuilderFiltersOperations.getViewpointBuilderSuggestionsLoaders(
          {
            relatedWorkspaces: [],
            organizationId: null,
            componentTypeName: null,
          }
        ),
      stateAsSavableAttributes: getTraversalAsSavableAttributes(initialState),
      perspectivesOptionsArgs: {
        workspaceIds: [],
        componentTypeNames: [],
        referenceTypeNames: [],
        availableComponentFields: [],
        availableReferenceFields: [],
        componentFieldsByType: new Map(),
        referenceFieldsByType: new Map(),
      },
      pathCollapsingViewState: {
        rules: [],
        currentEditableRule: null,
      },
      shouldShowDeleteCurrentNodeButton: false,
      shouldShowStartSetExpansionToggle: false,
      hierarchyComponentTypes: [],
      requiredComponentsState: null,
      shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings: false,
      shouldIncludeClickOtherComponentTypeNodesNotification: false,
    };
  };

export const resetTraversalBuilderState = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const clearedState = {
    // We need to keep the metamodel data, the according stream is persistent,
    // so there is no guarantee that the metamodel data would get added to the
    // TraversalBuilderState.
    ...state,
    filters: {},
    pathCollapsing: {
      rules: [],
      currentEditableRule: null,
    },
    traversalStartQuery: null,
    pathMatching: TraversalPathMatchingType.LOOSE,
    requiredNodeIds: [],
    highlightedPaths: {},
    selectedGraphNodeId: null,
    selectedGraphEdgeId: null,
    traversalStartSet: null,
    startSetCounts: null,
    startSetExpansion: null,
    startSetExpansionState: null,
    isInitialPath: true,
  };
  // As GraphNodes aren't cleared, we need to reset the instance counts.
  // This is to avoid showing old instance counts when in a context that shouldn't
  // show them (like when editing an existing viewpoint)
  return setInstanceCounts(clearedState, {
    startSetCount: -1,
    expandedStartSetCount: -1,
    traversalCounts: [],
  });
};
